// Paste + CSV contact parsers used by the Campaign wizard.
// Kept pure so the wizard can preview / dedup / validate without a
// server round-trip.
//
// The BACKEND is still the authoritative eligibility check
// (SuppressionService::canReceiveMarketing) — these parsers only
// produce { name, email, phone } shapes to POST to
// /campaigns/{id}/preview-external.

export interface ParsedContact { name?: string; email?: string; phone?: string; }

/** Loose paste parser: one contact per line, in a variety of shapes.
 *  Email campaign:  `john@x.co`  OR  `John Smith <john@x.co>`  OR
 *                   `John,john@x.co`
 *  SMS campaign:    `9051234567` OR  `Jane, 905-123-4567` OR
 *                   `+1 (905) 555-9999`
 */
export function parsePastedContacts(
  raw: string,
  channel: "email" | "sms",
): ParsedContact[] {
  const out: ParsedContact[] = [];
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  for (const line of lines) {
    if (channel === "email") {
      // `Name <email>` first — captures both.
      const angle = line.match(/^(.+?)\s*<\s*([^>]+)\s*>$/);
      if (angle) {
        out.push({ name: angle[1].trim(), email: angle[2].trim() });
        continue;
      }
      if (line.includes(",")) {
        const [a, b] = line.split(",").map((s) => s.trim());
        if (b && b.includes("@")) {
          out.push({ name: a, email: b });
          continue;
        }
        if (a && a.includes("@")) {
          out.push({ name: b || undefined, email: a });
          continue;
        }
      }
      if (line.includes("@")) {
        out.push({ email: line });
      }
    } else {
      // SMS: extract digits + optional preceding name.
      const nameMatch = line.match(/^(.+?)[,\s]+(\+?[\d().\s-]+)$/);
      if (nameMatch) {
        out.push({ name: nameMatch[1].trim(), phone: nameMatch[2].trim() });
        continue;
      }
      out.push({ phone: line });
    }
  }
  return out;
}

/** CSV parser — handles quoted fields with embedded commas and tolerates
 *  common header variations (first_name, last_name, full_name,
 *  email_address, mobile, phone_number). Rejects a file with no
 *  recognizable header. */
export function parseCsv(raw: string, channel: "email" | "sms"): ParsedContact[] {
  const rows = splitCsvRows(raw);
  if (rows.length === 0) return [];

  // Header detection — accept a header row OR a bare list of contacts
  // (single-column CSVs are common when a user just pastes emails).
  const first = rows[0].map((c) => c.trim().toLowerCase());
  const headerIndex = detectHeader(first);

  if (! headerIndex) {
    // Treat every row as either a single email or a single phone.
    return rows.flatMap((r) => r.filter(Boolean).map((v) => ({
      [channel === "email" ? "email" : "phone"]: v,
    } as ParsedContact)));
  }

  const dataRows = rows.slice(1);
  const out: ParsedContact[] = [];
  for (const row of dataRows) {
    const c: ParsedContact = {};
    if (headerIndex.name !== undefined) c.name = (row[headerIndex.name] || "").trim() || undefined;
    if (headerIndex.first !== undefined || headerIndex.last !== undefined) {
      const fn = (row[headerIndex.first ?? -1] || "").trim();
      const ln = (row[headerIndex.last  ?? -1] || "").trim();
      const combined = `${fn} ${ln}`.trim();
      if (combined) c.name = combined;
    }
    if (channel === "email" && headerIndex.email !== undefined) {
      c.email = (row[headerIndex.email] || "").trim() || undefined;
    }
    if (channel === "sms" && headerIndex.phone !== undefined) {
      c.phone = (row[headerIndex.phone] || "").trim() || undefined;
    }
    if (c.email || c.phone) out.push(c);
  }
  return out;
}

function detectHeader(cells: string[]): { name?: number; first?: number; last?: number; email?: number; phone?: number } | null {
  const idx: { name?: number; first?: number; last?: number; email?: number; phone?: number } = {};
  cells.forEach((c, i) => {
    if (["name", "full_name", "fullname"].includes(c))      idx.name  = i;
    else if (["first_name", "firstname", "first"].includes(c)) idx.first = i;
    else if (["last_name",  "lastname",  "last"].includes(c))  idx.last  = i;
    else if (["email", "email_address", "e-mail"].includes(c)) idx.email = i;
    else if (["phone", "mobile", "phone_number", "cell", "cellphone"].includes(c)) idx.phone = i;
  });
  return (idx.email !== undefined || idx.phone !== undefined || idx.name !== undefined || idx.first !== undefined) ? idx : null;
}

/** Minimal RFC-4180 CSV splitter — handles quoted fields with commas
 *  and doubled-quotes inside quotes. Not a full parser, but adequate
 *  for the school-contact CSVs this feature targets. */
function splitCsvRows(raw: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (inQuotes) {
      if (ch === '"') {
        if (raw[i + 1] === '"') { cell += '"'; i++; } else { inQuotes = false; }
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ",") { row.push(cell); cell = ""; }
      else if (ch === "\r") { /* skip */ }
      else if (ch === "\n") { row.push(cell); cell = ""; if (row.some((c) => c !== "")) rows.push(row); row = []; }
      else { cell += ch; }
    }
  }
  if (cell !== "" || row.length > 0) { row.push(cell); if (row.some((c) => c !== "")) rows.push(row); }
  return rows;
}
